import { HashRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { LoaderCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext.jsx";
import UserLayout from "../layouts/UserLayout.jsx";
import AdminLayout from "../layouts/AdminLayout.jsx";
import LoginPage from "../pages/auth/LoginPage.jsx";
import UserHome from "../pages/user/UserHome.jsx";
import AddByRoutine from "../pages/user/AddByRoutine.jsx";
import PendingOut from "../pages/user/PendingOut.jsx";
import UserSettings from "../pages/user/UserSettings.jsx";
import UserRoutineSettings from "../pages/user/UserRoutineSettings.jsx";
import AdminDashboard from "../pages/admin/AdminDashboard.jsx";
import RoutineBoardPage from "../pages/admin/RoutineBoardPage.jsx";
import RoutineManager from "../pages/admin/RoutineManager.jsx";
import TeacherManager from "../pages/admin/TeacherManager.jsx";
import SubjectManager from "../pages/admin/SubjectManager.jsx";
import ResourcesManager from "../pages/admin/ResourcesManager.jsx";
import TimeLogManager from "../pages/admin/TimeLogManager.jsx";
import ReportsPage from "../pages/admin/ReportsPage.jsx";
import SettingsPage from "../pages/admin/SettingsPage.jsx";

function FullPageLoader() {
  return (
    <main className="full-page-loader">
      <div className="app-mark">P</div>
      <LoaderCircle className="spin" size={28} />
      <p>Loading PARIKSIT Time...</p>
    </main>
  );
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin/dashboard" replace />;
  return <Navigate to="/user/home" replace />;
}

function RequireAuth({ role }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (role === "user" && user.role === "admin") return <Outlet />;
  if (role && user.role !== role) return <RoleRedirect />;
  return <Outlet />;
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <RoleRedirect />;
  return <LoginPage />;
}

export default function AppRouter() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/login" element={<LoginGate />} />
        <Route path="/user" element={<RequireAuth role="user" />}>
          <Route element={<UserLayout />}>
            <Route index element={<Navigate to="/user/home" replace />} />
            <Route path="home" element={<UserHome />} />
            <Route path="routine-board" element={<AddByRoutine />} />
            <Route path="pending" element={<PendingOut />} />
            <Route path="settings" element={<UserSettings />} />
            <Route path="routine-settings" element={<UserRoutineSettings />} />
            <Route path="profile" element={<Navigate to="/user/home" replace />} />
          </Route>
        </Route>
        <Route path="/admin" element={<RequireAuth role="admin" />}>
          <Route element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="routine-board" element={<RoutineBoardPage />} />
            <Route path="routines" element={<RoutineManager />} />
            <Route path="teachers" element={<TeacherManager />} />
            <Route path="subjects" element={<SubjectManager />} />
            <Route path="resources" element={<ResourcesManager />} />
            <Route path="logs" element={<TimeLogManager />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<RoleRedirect />} />
      </Routes>
    </HashRouter>
  );
}
